import React from 'react';
import ProgressBar from './ProgressBar';
import './CourseCard.css';

interface CourseCardProps {
  course: {
    id: string;
    title: string;
    instructor: string;
    thumbnail: string;
    progress: number; // 0-100
    status: 'Pending' | 'In Progress' | 'Completed';
    type: 'Live' | 'Self-Paced';
    dueDate?: string; // ISO 8601 date string
  };
  onNavigate: (courseId: string) => void;
}

const CourseCard: React.FC<CourseCardProps> = ({ course, onNavigate }) => {
  const { id, title, instructor, thumbnail, progress, status, dueDate } = course;

  // Check if due date is within 7 days
  const isDueSoon = () => {
    if (!dueDate) return false;
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= 7;
  };

  // Get status badge color
  const getStatusColor = () => {
    switch (status) {
      case 'Pending':
        return 'status-pending';
      case 'In Progress':
        return 'status-in-progress';
      case 'Completed':
        return 'status-completed';
      default:
        return 'status-pending';
    }
  };

  // Format due date
  const formatDueDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleClick = () => {
    onNavigate(id);
  };

  return (
    <div className="course-card" onClick={handleClick}>
      <div className="course-card-thumbnail">
        <img src={thumbnail} alt={title} />
      </div>
      
      <div className="course-card-content">
        <div className="course-card-header">
          <h3 className="course-card-title">{title}</h3>
          <span className={`course-card-status ${getStatusColor()}`}>
            {status}
          </span>
        </div>

        <p className="course-card-instructor">By {instructor}</p>

        <div className="course-card-progress">
          <ProgressBar progress={progress} height={6} showLabel={true} />
        </div>

        {dueDate && (
          <div className={`course-card-due-date ${isDueSoon() ? 'due-soon' : ''}`}>
            <svg 
              className="due-date-icon" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
              />
            </svg>
            <span>Due: {formatDueDate(dueDate)}</span>
          </div>
        )}

        <button className="course-card-button">
          Go to Course
        </button>
      </div>
    </div>
  );
};

export default CourseCard;
